import React, { useEffect, useMemo, useState } from 'react';
import './_translations.list.scss';
import './_translations.list.dark-mode.scss';
import PropTypes from 'prop-types';
import { Badge, Button, ControlLabel, FormControl, FormGroup } from 'react-bootstrap';
import { AutoSizer, List } from 'react-virtualized';
import { inRange, isEqual } from 'lodash';
import classNames from 'classnames';
import { useHistory, useLocation } from 'react-router-dom';
import SearchInput from '../../../../../../components/Search/SearchInput';
import { createGroups } from '../../helpers';
import { getHighlightingText } from '../../../../../../utils/helper';
import { routesAppearance } from '../../../../helpers';

const TranslationsList = ({
  config,
  can_delete,
  can_reset,
  translations,
  name,
  isDefault,
  isLoading,
  onSaveChanges,
  onDelete,
  onReset,
  onMakeDefault,
}) => {
  const location = useLocation();
  const history = useHistory();

  const groups = useMemo(() => createGroups(translations), [translations])
  const [search, setSearch] = useState('');
  const [languageTranslations, setLanguageTranslations] = useState(translations);
  const [languageName, setLanguageName] = useState(name || '');
  const [scrollToIndex, setScrollToIndex] = useState(0);
  const [activeScrollIndex, setActiveScrollIndex] = useState(1);
  const [rowRender, setRowRender] = useState({
    startIndex: 0,
    stopIndex: 0,
  })
  const listAfterSearch = useMemo(() => languageTranslations.reduce((accumulator, currentValue, index) => {
    if (!search) {
      return accumulator;
    }
    if (currentValue.key.toLowerCase().indexOf(search.toLowerCase()) !== -1 || currentValue.value.toLowerCase().indexOf(search.toLowerCase()) !== -1) {
      accumulator.push(index)
    }
    return accumulator;
  }, []), [search, languageTranslations])

  const languagesRoute = useMemo(() => routesAppearance.find((route) => route.code === 'languages'), [])

  useEffect(() => {
    if (name !== languageName) {
      setLanguageName(name)
    }
    if (!isEqual(translations, languageTranslations)) {
      setLanguageTranslations(translations)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [name, translations])

  useEffect(() => {
    if (listAfterSearch.length && activeScrollIndex !== 0) {
      setScrollToIndex(listAfterSearch[activeScrollIndex - 1])
      history.push({
        pathname: location.pathname,
        search: location.search,
        hash: `#${languageTranslations[listAfterSearch[activeScrollIndex - 1]].key}`,
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeScrollIndex])

  useEffect(() => {
    setActiveScrollIndex(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search])

  useEffect(() => {
    if (location.hash) {
      const key = location.hash.replace('#', '');
      const findTranslationIndex = languageTranslations.findIndex((translation) => translation.key === key);
      if (findTranslationIndex !== -1) {
        setScrollToIndex(findTranslationIndex + 1)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash])

  const onScrollToGroup = (index) => {
    const hash = languageTranslations[index];
    if (hash) {
      history.push({
        pathname: location.pathname,
        search: location.search,
        hash: `#${hash.key}`,
      })
    }
    setScrollToIndex(index)
  }

  const onChangeTranslation = (index, value) => {
    setLanguageTranslations((prevState) => prevState.map((translation, currentIndex) => {
      if (currentIndex === index) {
        return {
          ...translation,
          value,
        }
      }
      return translation;
    }))
  }

  const onSubmit = () => {
    const messages = languageTranslations.reduce((accumulator, currentValue) => {
      accumulator[currentValue.key] = currentValue.value;
      return accumulator;
    }, {})
    onSaveChanges(languageName, messages)
  }

  const rowRenderer = ({ index, key, style }) => {
    const translation = languageTranslations[index - 1];
    if (index === 0) {
      return (
        <div className="translations-list__panel-fields-row" key={key} style={style}>
          <FormGroup>
            <ControlLabel>Name</ControlLabel>
            <FormControl
              type="text"
              value={languageName}
              onChange={(e) => setLanguageName(e.target.value)}
            />
          </FormGroup>
          <hr />
        </div>
      )
    }
    return (
      <div className="translations-list__panel-fields-row" key={key} style={style}>
        <FormGroup>
          <ControlLabel>
            <span dangerouslySetInnerHTML={getHighlightingText(translation.key, search, true)} />
          </ControlLabel>
          <FormControl
            type="text"
            className={classNames({
              'translations-list__panel-fields-highlight': search ? translation.value.toLowerCase().includes(search.toLowerCase()) : false,
            })}
            value={translation.value}
            placeholder={translation.value}
            onChange={(e) => onChangeTranslation(index - 1, e.target.value)}
          />
        </FormGroup>
      </div>
    );
  };

  const onChangeActiveScroll = () => {
    const allCounts = listAfterSearch.length + 1;
    const nextScrollIndex = activeScrollIndex + 1;
    if (nextScrollIndex >= allCounts) {
      setActiveScrollIndex(1)
    } else {
      setActiveScrollIndex(activeScrollIndex + 1)
    }
  }
  const scrollToNextIndex = (event) => {
    if (event.key === 'Enter') {
      onChangeActiveScroll()
    }
  }

  const isGroupActive = (index) => {
    const firstIndex = groups[index];
    const nextIndex = index >= groups.length - 1 ? groups[index] : groups[index + 1];
    if (rowRender.startIndex === 0 && index === 0) {
      return true;
    }
    return inRange(rowRender.startIndex, firstIndex.index, index >= groups.length - 1 ? languageTranslations.length : nextIndex.index)
  }

  const onBack = () => {
    history.push(languagesRoute.link)
  }

  return (
    <div className="translations-list">
      {isLoading ? <div className="modal-loader" /> : null }
      <div className="translations-list__panel">
        <div className="translations-list__panel-header">
          <div className="translations-list__panel-title">
            <div className="translations-list__panel-back">
              <i onClick={onBack} title="Languages list" className="fas fa-chevron-left" />
            </div>
            {name}
            {isDefault ? <Badge>Default</Badge> : <Button bsSize="xsmall" onClick={onMakeDefault} disabled={isLoading}>Make default</Button> }
          </div>
          <div className="translations-list__panel-search">
            <SearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search"
              onKeyDown={scrollToNextIndex}
            />
            {listAfterSearch.length || search
              ? (
                <div className="translations-list__panel-search-count" onClick={onChangeActiveScroll}>
                  <Badge>{listAfterSearch.length ? `${activeScrollIndex === 0 ? 1 : activeScrollIndex}/${listAfterSearch.length}` : 0}</Badge>
                </div>
              ) : null}
          </div>
        </div>
        <div className="translations-list__panel-body">

          <div className="translations-list__panel-list">
            <div className="translations-list__menu">
              {groups.map((group, index) => (
                <div
                  className={classNames('translations-list__menu-item', {
                    'translations-list__menu-item-active': isGroupActive(index),
                  })}
                  key={`menu-${group.name}`}
                  onClick={() => onScrollToGroup(group.index)}
                >
                  {group.name}
                </div>
              ))}
            </div>
          </div>
          <div className="translations-list__panel-content">
            <div className="translations-list__panel-fields">
              <AutoSizer>
                {({ width, height }) => (
                  <List
                    onRowsRendered={({ startIndex, stopIndex }) => {
                      setRowRender({
                        startIndex,
                        stopIndex,
                      })
                    }}
                    scrollToAlignment="start"
                    scrollToIndex={scrollToIndex}
                    className="translations-list__panel-fields-list"
                    height={height}
                    rowCount={languageTranslations.length + 1}
                    rowHeight={({ index }) => (!index ? 90 : 75)}
                    rowRenderer={rowRenderer}
                    width={width}
                  />
                )}
              </AutoSizer>
            </div>
            <div className="translations-list__panel-footer">
              <div>
                {can_delete && config?.access?.languages_delete ? (
                  <div>
                    <Button
                      disabled={isLoading}
                      onClick={onDelete}
                      bsStyle="link"
                      block
                    >
                      Delete language
                    </Button>
                  </div>
                ) : null }
              </div>
              <div className="translations-list__panel-footer-right">
                {config?.access?.languages_reset && can_reset ? (
                  <div>
                    <Button
                      disabled={isLoading}
                      onClick={onReset}
                      bsStyle="link"
                      block
                    >
                      Reset changes
                    </Button>
                  </div>
                ) : null }
                <div>
                  <Button
                    disabled={isLoading}
                    onClick={onSubmit}
                    bsStyle="primary"
                    block
                  >
                    Save changes
                  </Button>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </div>
  )
};

TranslationsList.propTypes = {
  config: PropTypes.object,
  isLoading: PropTypes.bool,
  onSaveChanges: PropTypes.func,
  onReset: PropTypes.func,
  onDelete: PropTypes.func,
  onMakeDefault: PropTypes.func,
  can_delete: PropTypes.bool,
  can_reset: PropTypes.bool,
  translations: PropTypes.array,
  name: PropTypes.string,
  isDefault: PropTypes.bool,
};

export default TranslationsList;
